import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { DocumentEndpointClient } from '../../../../../../repository/UserManagement/Managers/DocumentServiceClientPb';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession, ACCOUNTANT_API_URL } from '../../../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../../../app/Helpers';
import { TableState } from '../../../../../common/Table/TableSate';
import { number } from 'yup';
import { DeleteDocumentRequest, DeleteDocumentResponse, GenerateSecureLinkRequest, GenerateSecureLinkResponse, GetDocumentRequest, GetDocumentsRequest, GetDocumentsResponse } from '../../../../../../repository/UserManagement/Managers/document_pb';


export interface DocumentsPageState {
    set: TableState
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    searchText: string | undefined,
    filters: any[],
    expirationFilterOption: { value: string, text: string },
    filePreview: string | undefined

}

const initialState: DocumentsPageState = {
    set: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },
    isChangeStateLoading: false,
    message: undefined,
    searchText: undefined,
    filters: [],
    expirationFilterOption: { value: "", text: "" },
    filePreview: undefined

}

const client = new DocumentEndpointClient(API_URL, null, null);


export const getItems = createAsyncThunk<GetDocumentsResponse.AsObject, APIRequest<GetDocumentsRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/management/userDetails/documents/fetch',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {

                var r = await client.getDocuments(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)


export const deleteItem = createAsyncThunk<APIResponse<DeleteDocumentResponse.AsObject>, APIRequest<DeleteDocumentRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/management/userDetails/documents/delete',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.delete(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)

export const generateDownloadLink = createAsyncThunk<APIResponse<GenerateSecureLinkResponse.AsObject>, APIRequest<GenerateSecureLinkRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/management/userDetails/documents/generateLink',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.generateSecureLink(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)






const getFilters = (state: DocumentsPageState): any[] => {
    const selected = [];

    if (state.expirationFilterOption.value && state.expirationFilterOption.value != "") {
        selected.push({ key: 1, name: state.expirationFilterOption.text });
    }

    return selected;
}

export const documentsPageSlice = createSlice({
    name: 'pages/management/userDetails/documents',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.set = initialState.set;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.searchText = initialState.searchText;
            state.filters = initialState.filters;
            state.expirationFilterOption = initialState.expirationFilterOption;
            state.filePreview = initialState.filePreview;

        },
        resetSet: (state) => {
            state.set.items = [];
            state.set.hasMore = true;
        },


        setSort: (state, action: PayloadAction<boolean>) => {
            state.set.isDescending = action.payload;
        },
        setNumberOfResults: (state, action: PayloadAction<number>) => {
            state.set.numberOfResults = action.payload;
        },
        setSelectedItems: (state, action: PayloadAction<any[]>) => {
            state.set.selected = action.payload;
        },


        setMessage: (state, action: PayloadAction<any>) => {
            state.message = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.searchText = action.payload;
        },
        setIsFilteredSet: (state, action: PayloadAction<boolean>) => {
            state.set.isFilteredSet = action.payload;
        },

        setFilePreview: (state, action: PayloadAction<string | undefined>) => {
            state.filePreview = action.payload;
        },
        setExpirationFilterOption: (state, action: PayloadAction<{ value: string, text: string }>) => {
            state.expirationFilterOption = action.payload;
            state.filters = getFilters(state);
        },
        addItem: (state, action: PayloadAction<any>) => {
            if (!state.set.isFilteredSet) {
                if (!state.set.hasMore && !state.set.isDescending) {
                    state.set.items.push(action.payload);
                } else if (state.set.isDescending) {
                    state.set.items.unshift(action.payload);
                }
            }
        },
        updateItem: (state, action: PayloadAction<any>) => {
            let selected = state.set.selected;
            if (action?.payload) {
                if (selected.length > 0 && selected.at(0).id == action?.payload?.id) {
                    selected[0] = action?.payload
                }
                var u = state.set.items.findIndex(e => e.id == action?.payload?.id)
                if (u >= 0) {
                    state.set.items[u] = action?.payload
                }
            }

        }
    },

    extraReducers: (builder) => {
        builder.addCase(getItems.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.documentsList.map(val => {
                    return {
                        id: val.id,
                        name: val.name?.value ?? "",
                        ext: val.ext?.value ?? "",
                        originalName: val.originalname?.value ?? "",
                        ownerFirstName: val.owner?.firstname?.value ?? "",
                        ownerLastName: val.owner?.lastname?.value ?? "",
                        ownerMiddleName: val.owner?.middlename?.value ?? "",

                        ownerId: val.owner?.id?.value,
                        size: val.size?.value ?? undefined,
                        expirationDate: val?.expirationdate ? formatDate(timestampToDate(val?.expirationdate?.seconds, val?.expirationdate?.nanos), "YYYY-MM-DD") : undefined,

                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                        addedById: val.createdby?.id?.value,
                    }
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                let l = [];
                if (state.set.items.length != 0) {
                    l = state.set.items.slice(0, state.set.items.length - 10)
                }
                l = l.concat(r);
                if (r.length < state.set.numberOfResults) {
                    state.set.hasMore = false;
                } else {
                    l = l.concat(...Array(10).fill(null));
                }
                state.set.items = l
            } else {
                state.set.hasMore = false;
            }
            state.set.isFetching = false

        })
        builder.addCase(getItems.rejected, (state, action) => {
            state.set.isFetching = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getItems.pending, (state, action) => {
            state.set.isFetching = true;
            state.message = undefined;
        })



        builder.addCase(deleteItem.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                let selected = state.set.selected;
                if (payload?.metadata) {
                    if (selected.length > 0 && selected.at(0).id == payload?.metadata.id) {
                        state.set.selected = []
                    }
                    var u = state.set.items.findIndex(e => e.id == payload?.metadata.id)
                    if (u >= 0) {
                        state.set.items.splice(u, 1);
                    }
                }
            }
        })
        builder.addCase(deleteItem.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(deleteItem.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })

        builder.addCase(generateDownloadLink.fulfilled, (state, { payload }) => {
            if (payload) {
                state.filePreview = payload.response.success?.resourceuri?.value
            }

        })
        builder.addCase(generateDownloadLink.rejected, (state, action) => {
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(generateDownloadLink.pending, (state, action) => {
            state.message = undefined;
        })




    }
})

export const { reset, dismissMessage, setSort, setFilePreview, setExpirationFilterOption, setNumberOfResults, setMessage, resetSet, setSelectedItems, setSearchText, setIsFilteredSet, addItem, updateItem, } = documentsPageSlice.actions

export default documentsPageSlice.reducer


